import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import useRealtimeSubscription from '../../hooks/useRealtimeSubscription';
import RoleNavigation from '../../components/ui/RoleNavigation';
import NotificationCenter from '../../components/ui/NotificationCenter';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import BrandSelector from './components/BrandSelector';
import PurchaseValueInput from './components/PurchaseValueInput';
import ReceiptUploader from './components/ReceiptUploader';
import SocialPostInput from './components/SocialPostInput';
import AdditionalDetails from './components/AdditionalDetails';
import SubmissionChecklist from './components/SubmissionChecklist';
import ProcessingInfo from './components/ProcessingInfo';
import SubmissionSuccess from './components/SubmissionSuccess';

const TransactionSubmissionScreen = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [trackingId, setTrackingId] = useState('');
  const [submittedTransactionId, setSubmittedTransactionId] = useState(null);
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [brands, setBrands] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(true);

  const [formData, setFormData] = useState({
    brandId: '',
    purchaseAmount: '',
    receipt: null,
    socialPostUrl: '',
    purchaseDate: '',
    category: '',
    hashtag: ''
  });

  const [errors, setErrors] = useState({});

  // Load active campaigns as brand options
  const loadBrands = useCallback(async () => {
    try {
      const { data, error } = await supabase?.from('campaigns')?.select('id, title, description, reward_amount, budget, categories')?.eq('status', 'active')?.order('created_at', { ascending: false });

      if (!error && data) {
        const mapped = data?.map(c => ({
          id: c?.id,
          name: c?.title,
          category: c?.categories?.[0] || 'General',
          cashbackPercentage: parseFloat(c?.reward_amount) || 0,
          rewardCap: parseFloat(c?.budget) || 0,
        }));
        setBrands(mapped);
      }
    } catch (err) {
      console.error('[TransactionSubmission] loadBrands error:', err);
    } finally {
      setLoadingBrands(false);
    }
  }, []);

  useEffect(() => {
    loadBrands();
  }, []);

  // Real-time: track submission status after submit
  useRealtimeSubscription({
    channelName: `submission-status-${submittedTransactionId}`,
    table: 'transactions',
    filter: submittedTransactionId ? `id=eq.${submittedTransactionId}` : undefined,
    enabled: !!submittedTransactionId,
    onUpdate: (payload) => {
      const newStatus = payload?.new?.status;
      if (newStatus) {
        setSubmissionStatus(newStatus);
      }
    },
  });

  const selectedBrand = brands?.find(b => b?.id === formData?.brandId);

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.brandId) newErrors.brandId = 'Please select a brand partner';
    if (!formData?.purchaseAmount || parseFloat(formData?.purchaseAmount) <= 0)
      newErrors.purchaseAmount = 'Please enter a valid purchase amount';
    if (!formData?.receipt) newErrors.receipt = 'Please upload your receipt';
    if (!formData?.socialPostUrl) {
      newErrors.socialPostUrl = 'Please provide your social media post URL';
    } else {
      const urlPattern = /(instagram\.com|tiktok\.com|youtube\.com|youtu\.be|twitter\.com|x\.com)/i;
      if (!urlPattern?.test(formData?.socialPostUrl))
        newErrors.socialPostUrl = 'Please provide a valid social media URL';
    }
    if (!formData?.purchaseDate) newErrors.purchaseDate = 'Please select the purchase date';
    if (!formData?.category) newErrors.category = 'Please select a product category';
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;
    if (!user?.id) return;

    setIsSubmitting(true);
    try {
      const selectedCampaign = brands?.find(b => b?.id === formData?.brandId);
      const purchaseVal = parseFloat(formData?.purchaseAmount) || 0;
      const cashbackPct = selectedCampaign?.cashbackPercentage || 0;
      const rewardCap = selectedCampaign?.rewardCap || 0;
      const calculatedReward = Math.min(purchaseVal * (cashbackPct / 100), rewardCap);

      const { data, error } = await supabase?.from('transactions')?.insert({
          influencer_id: user?.id,
          campaign_id: formData?.brandId,
          amount: calculatedReward,
          purchase_value: purchaseVal,
          social_post_url: formData?.socialPostUrl,
          notes: formData?.hashtag || '',
          status: 'pending',
          transaction_type: 'purchase',
        })?.select('id')?.single();

      if (error) {
        console.error('[TransactionSubmission] submit error:', error);
        setErrors({ submit: error?.message });
        setIsSubmitting(false);
        return;
      }

      const newTrackingId = `SC${data?.id?.slice(-8)?.toUpperCase()}`;
      setTrackingId(newTrackingId);
      setSubmittedTransactionId(data?.id);
      setSubmissionStatus('pending');
      setIsSubmitting(false);
      setShowSuccess(true);
    } catch (err) {
      console.error('[TransactionSubmission] handleSubmit error:', err);
      setErrors({ submit: 'An unexpected error occurred. Please try again.' });
      setIsSubmitting(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
    setSubmittedTransactionId(null);
    setSubmissionStatus(null);
    setFormData({
      brandId: '',
      purchaseAmount: '',
      receipt: null,
      socialPostUrl: '',
      purchaseDate: '',
      category: '',
      hashtag: ''
    });
    setErrors({});
  };

  const handleViewStatus = () => {
    navigate('/user-dashboard');
  };

  const isFormComplete =
    formData?.brandId &&
    formData?.purchaseAmount &&
    formData?.receipt &&
    formData?.socialPostUrl &&
    formData?.purchaseDate &&
    formData?.category;

  const estimatedApproval = new Date(Date.now() + 2 * 24 * 60 * 60 * 1000)?.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });

  const displayName = userProfile?.full_name || user?.user_metadata?.full_name || 'User';

  return (
    <div className="min-h-screen bg-background">
      <RoleNavigation role="influencer" userName={displayName} />
      <div className="pt-[60px]">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
          <div className="flex items-center justify-between mb-6 md:mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
                Submit Purchase
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Claim your cashback rewards by sharing your purchase experience
              </p>
            </div>
            <NotificationCenter role="influencer" />
          </div>

          {/* Real-time status tracker after submission */}
          {submittedTransactionId && !showSuccess && submissionStatus && (
            <div className={`mb-6 p-4 rounded-xl border flex items-center gap-3 ${
              submissionStatus === 'approved' ? 'bg-success/10 border-success/30' :
              submissionStatus === 'rejected'? 'bg-error/10 border-error/30' : 'bg-primary/10 border-primary/30'
            }`}>
              <Icon
                name={submissionStatus === 'approved' ? 'CheckCircle' : submissionStatus === 'rejected' ? 'XCircle' : 'Clock'}
                size={20}
                color={submissionStatus === 'approved' ? 'var(--color-success)' : submissionStatus === 'rejected' ? 'var(--color-error)' : 'var(--color-primary)'}
              />
              <div>
                <p className="text-sm font-semibold text-foreground">
                  Submission Status: {submissionStatus?.charAt(0)?.toUpperCase() + submissionStatus?.slice(1)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {submissionStatus === 'pending' ? 'Your submission is under review. Updates will appear here in real-time.' :
                   submissionStatus === 'approved' ? 'Your submission has been approved! Cashback will be credited to your wallet.' :
                   'Your submission was rejected. Please check your dashboard for details.'}
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="space-y-6 md:space-y-8">
                <div className="bg-card rounded-xl p-4 md:p-6 lg:p-8 border border-border">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon name="Building2" size={20} color="var(--color-primary)" />
                    </div>
                    <h2 className="text-lg md:text-xl font-semibold text-foreground">
                      Brand Selection
                    </h2>
                  </div>
                  {loadingBrands ? (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      <span className="text-sm">Loading campaigns...</span>
                    </div>
                  ) : (
                    <BrandSelector
                      brands={brands}
                      selectedBrand={formData?.brandId}
                      onBrandChange={(value) => setFormData({ ...formData, brandId: value })}
                      error={errors?.brandId}
                    />
                  )}
                </div>

                <div className="bg-card rounded-xl p-4 md:p-6 lg:p-8 border border-border">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                      <Icon name="DollarSign" size={20} color="var(--color-success)" />
                    </div>
                    <h2 className="text-lg md:text-xl font-semibold text-foreground">
                      Purchase Details
                    </h2>
                  </div>
                  <PurchaseValueInput
                    value={formData?.purchaseAmount}
                    onChange={(value) => setFormData({ ...formData, purchaseAmount: value })}
                    cashbackPercentage={selectedBrand?.cashbackPercentage || 0}
                    rewardCap={selectedBrand?.rewardCap || 0}
                    error={errors?.purchaseAmount}
                  />
                </div>

                <div className="bg-card rounded-xl p-4 md:p-6 lg:p-8 border border-border">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                      <Icon name="FileText" size={20} color="var(--color-secondary)" />
                    </div>
                    <h2 className="text-lg md:text-xl font-semibold text-foreground">
                      Receipt Upload
                    </h2>
                  </div>
                  <ReceiptUploader
                    receipt={formData?.receipt}
                    onReceiptChange={(file) => setFormData({ ...formData, receipt: file })}
                    error={errors?.receipt}
                  />
                </div>

                <div className="bg-card rounded-xl p-4 md:p-6 lg:p-8 border border-border">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                      <Icon name="Share2" size={20} color="var(--color-warning)" />
                    </div>
                    <h2 className="text-lg md:text-xl font-semibold text-foreground">
                      Social Media Post
                    </h2>
                  </div>
                  <SocialPostInput
                    value={formData?.socialPostUrl}
                    onChange={(value) => setFormData({ ...formData, socialPostUrl: value })}
                    error={errors?.socialPostUrl}
                  />
                </div>

                <div className="bg-card rounded-xl p-4 md:p-6 lg:p-8 border border-border">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon name="Info" size={20} color="var(--color-primary)" />
                    </div>
                    <h2 className="text-lg md:text-xl font-semibold text-foreground">
                      Additional Information
                    </h2>
                  </div>
                  <AdditionalDetails
                    purchaseDate={formData?.purchaseDate}
                    onPurchaseDateChange={(value) => setFormData({ ...formData, purchaseDate: value })}
                    category={formData?.category}
                    onCategoryChange={(value) => setFormData({ ...formData, category: value })}
                    hashtag={formData?.hashtag}
                    onHashtagChange={(value) => setFormData({ ...formData, hashtag: value })}
                    errors={errors}
                  />
                </div>

                {errors?.submit && (
                  <div className="p-4 rounded-xl bg-error/10 border border-error/30 flex items-center gap-3">
                    <Icon name="AlertCircle" size={16} color="var(--color-error)" />
                    <p className="text-sm text-error">{errors?.submit}</p>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    iconName="ArrowLeft"
                    onClick={() => navigate('/user-dashboard')}
                    fullWidth>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="default"
                    iconName="Send"
                    iconPosition="right"
                    loading={isSubmitting}
                    disabled={!isFormComplete || isSubmitting}
                    fullWidth>
                    {isSubmitting ? 'Submitting...' : 'Submit for Review'}
                  </Button>
                </div>
              </form>
            </div>

            <div className="space-y-6">
              <SubmissionChecklist
                brandSelected={!!formData?.brandId}
                purchaseAmount={!!formData?.purchaseAmount}
                receiptUploaded={!!formData?.receipt}
                socialPostUrl={!!formData?.socialPostUrl}
                purchaseDate={!!formData?.purchaseDate}
                category={!!formData?.category}
              />
              <ProcessingInfo />
            </div>
          </div>
        </div>
      </div>
      {showSuccess && (
        <SubmissionSuccess
          trackingId={trackingId}
          estimatedApproval={estimatedApproval}
          onClose={handleSuccessClose}
          onViewStatus={handleViewStatus}
        />
      )}
    </div>
  );
};

export default TransactionSubmissionScreen;